/**
 * AnalyticsTab Component
 * Displays Google Analytics data with charts and visualizations
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { FiRefreshCw, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import useAnalyticsData from '../hooks/useAnalyticsData';

// Responsive container to replace ResponsiveContainer and avoid Router context issues
const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || 300}px;
  position: relative;
  overflow: hidden;
  
  svg {
    max-width: 100%;
    height: auto;
  }
`;

const AnalyticsTab = ({ refreshKey }) => {
  const [componentError, setComponentError] = useState(null);
  const [chartWidth, setChartWidth] = useState(800);
  
  // Calculate chart width based on container
  useEffect(() => {
    const updateWidth = () => {
      const container = document.querySelector('[data-chart-container]');
      if (container) {
        setChartWidth(Math.min(container.offsetWidth - 100, 800));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  // Hooks must be called unconditionally
  const {
    pageViews,
    topPages,
    demographics,
    realTime,
    loading,
    error,
    fetchPageViews,
    refreshAll
  } = useAnalyticsData();

  const [dateRange, setDateRange] = useState({
    startDate: '30daysAgo',
    endDate: 'today'
  });

  // Fetch data on mount and when refreshKey changes
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        await refreshAll(dateRange.startDate, dateRange.endDate);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        if (isMounted) {
          setComponentError(err.message || 'Failed to load analytics data');
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    let startDate, endDate;
    
    switch (value) {
      case '7days':
        startDate = '7daysAgo';
        endDate = 'today';
        break;
      case '30days':
        startDate = '30daysAgo';
        endDate = 'today';
        break;
      case '90days':
        startDate = '90daysAgo';
        endDate = 'today';
        break;
      default:
        startDate = '30daysAgo';
        endDate = 'today';
    }
    
    setDateRange({ startDate, endDate });
    fetchPageViews(startDate, endDate);
  };

  const handleRefresh = () => {
    refreshAll(dateRange.startDate, dateRange.endDate);
    toast.info('Refreshing analytics data...');
  };

  // Prepare data for charts with safe access
  const pageViewsData = useMemo(() => {
    if (!pageViews?.data || !Array.isArray(pageViews.data)) {
      return [];
    }
    try {
      return pageViews.data.reduce((acc, item) => {
        if (!item || !item.date) return acc;
        const existing = acc.find(d => d.date === item.date);
        if (existing) {
          existing.pageViews += (item.pageViews || 0);
          existing.activeUsers += (item.activeUsers || 0);
        } else {
          acc.push({
            date: item.date,
            pageViews: item.pageViews || 0,
            activeUsers: item.activeUsers || 0
          });
        }
        return acc;
      }, []);
    } catch (err) {
      console.error('Error processing page views data:', err);
      return [];
    }
  }, [pageViews]);

  const topPagesData = useMemo(() => {
    if (!topPages?.data || !Array.isArray(topPages.data)) {
      return [];
    }
    try {
      return topPages.data.slice(0, 10);
    } catch (err) {
      console.error('Error processing top pages data:', err);
      return [];
    }
  }, [topPages]);

  const demographicsByCountry = useMemo(() => {
    // Prefer real-time country data if available
    if (realTime?.data?.byCountry && Array.isArray(realTime.data.byCountry) && realTime.data.byCountry.length > 0) {
      try {
        return realTime.data.byCountry.map(item => ({
          country: item.country || 'Unknown',
          activeUsers: item.activeUsers || 0,
          sessions: item.activeUsers || 0 // Use activeUsers as sessions for real-time
        })).filter(item => item.activeUsers > 0);
      } catch (err) {
        console.error('Error processing real-time country data:', err);
      }
    }
    
    // Fall back to demographics data
    if (!demographics?.data || !Array.isArray(demographics.data)) {
      return [];
    }
    try {
      return demographics.data.reduce((acc, item) => {
        if (!item || !item.country) return acc;
        const existing = acc.find(d => d.country === item.country);
        if (existing) {
          existing.activeUsers += (item.activeUsers || 0);
          existing.sessions += (item.sessions || 0);
        } else {
          acc.push({
            country: item.country,
            activeUsers: item.activeUsers || 0,
            sessions: item.sessions || 0
          });
        }
        return acc;
      }, []);
    } catch (err) {
      console.error('Error processing demographics data:', err);
      return [];
    }
  }, [realTime, demographics]);

  const deviceCategoryData = useMemo(() => {
    // Prefer real-time device data if available
    if (realTime?.data?.byDevice && Array.isArray(realTime.data.byDevice) && realTime.data.byDevice.length > 0) {
      try {
        return realTime.data.byDevice.map(item => ({
          device: item.deviceCategory || 'Unknown',
          value: item.activeUsers || 0
        })).filter(item => item.value > 0);
      } catch (err) {
        console.error('Error processing real-time device data:', err);
      }
    }
    
    // Fall back to demographics data
    if (!demographics?.data || !Array.isArray(demographics.data)) {
      return [];
    }
    try {
      return demographics.data.reduce((acc, item) => {
        if (!item || !item.deviceCategory) return acc;
        const existing = acc.find(d => d.device === item.deviceCategory);
        if (existing) {
          existing.value += (item.activeUsers || 0);
        } else {
          acc.push({
            device: item.deviceCategory,
            value: item.activeUsers || 0
          });
        }
        return acc;
      }, []);
    } catch (err) {
      console.error('Error processing device category data:', err);
      return [];
    }
  }, [realTime, demographics]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  // Show error if component error occurred
  if (componentError) {
    return (
      <ErrorContainer>
        <ErrorMessage>
          <p>Failed to load analytics</p>
          <p>{componentError}</p>
          <RefreshButton onClick={() => window.location.reload()}>
            <FiRefreshCw /> Reload Page
          </RefreshButton>
        </ErrorMessage>
      </ErrorContainer>
    );
  }

  // Show error message if all requests failed and we have an error
  if (error && !pageViews && !topPages && !demographics && !realTime && !loading.pageViews && !loading.topPages && !loading.demographics && !loading.realTime) {
    const isConfigurationError = error.includes('not configured') || error.includes('unavailable') || error.includes('Analytics service');
    
    return (
      <ErrorContainer>
        <ErrorMessage>
          <p><strong>Google Analytics is not configured</strong></p>
          {isConfigurationError ? (
            <>
              <p>Please configure Google Analytics on the server:</p>
              <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '1rem' }}>
                <li>Ensure GA_PROPERTY_ID is set in server/.env</li>
                <li>Ensure GA_CREDENTIALS_PATH points to valid service account JSON</li>
                <li>Verify the service account has access to the GA4 property</li>
                <li>Run: <code>node scripts/verify-analytics-setup.js</code> to diagnose</li>
              </ul>
            </>
          ) : (
            <p>{error}</p>
          )}
          <RefreshButton onClick={handleRefresh} style={{ marginTop: '1rem' }}>
            <FiRefreshCw /> Retry
          </RefreshButton>
        </ErrorMessage>
      </ErrorContainer>
    );
  }

  return (
    <Container data-chart-container>
      <Header>
        <Title>Google Analytics Dashboard</Title>
        <Controls>
          <DateRangeSelect value={dateRange.startDate === '7daysAgo' ? '7days' : dateRange.startDate === '90daysAgo' ? '90days' : '30days'} onChange={handleDateRangeChange}>
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
          </DateRangeSelect>
          <RefreshButton onClick={handleRefresh} disabled={loading.pageViews || loading.topPages || loading.demographics || loading.realTime}>
            <FiRefreshCw /> Refresh
          </RefreshButton>
        </Controls>
      </Header>

      {/* Real-time Stats */}
      {realTime && (
        <StatsGrid>
          <StatCard>
            <StatIcon>
              <FiUsers />
            </StatIcon>
            <StatContent>
              <StatValue>{realTime.data?.totalActiveUsers || 0}</StatValue>
              <StatLabel>Active Users Now</StatLabel>
            </StatContent>
          </StatCard>
        </StatsGrid>
      )}

      {/* Page Views Chart */}
      <ChartCard>
        <ChartTitle>Page Views Over Time</ChartTitle>
        {loading.pageViews ? (
          <LoadingMessage>Loading page views...</LoadingMessage>
        ) : pageViewsData.length > 0 ? (
          <ChartContainer height={300}>
            <LineChart width={chartWidth} height={300} data={pageViewsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="pageViews" stroke="#8884d8" name="Page Views" />
              <Line type="monotone" dataKey="activeUsers" stroke="#82ca9d" name="Active Users" />
            </LineChart>
          </ChartContainer>
        ) : (
          <EmptyMessage>
            No page views data available
            <br />
            <small style={{ fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
              Historical data may take 24-48 hours to appear in Google Analytics
            </small>
          </EmptyMessage>
        )}
      </ChartCard>

      {/* Top Pages Chart */}
      <ChartCard>
        <ChartTitle>Top Pages</ChartTitle>
        {loading.topPages ? (
          <LoadingMessage>Loading top pages...</LoadingMessage>
        ) : topPagesData.length > 0 ? (
          <ChartContainer height={400}>
            <BarChart width={chartWidth} height={400} data={topPagesData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="pageTitle" type="category" width={200} />
              <Tooltip />
              <Legend />
              <Bar dataKey="pageViews" fill="#8884d8" name="Page Views" />
            </BarChart>
          </ChartContainer>
        ) : (
          <EmptyMessage>
            No top pages data available
            <br />
            <small style={{ fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
              Historical data may take 24-48 hours to appear in Google Analytics
            </small>
          </EmptyMessage>
        )}
      </ChartCard>

      {/* Demographics Charts */}
      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Users by Country {realTime?.data?.byCountry ? '(Real-time)' : ''}</ChartTitle>
          {loading.realTime || loading.demographics ? (
            <LoadingMessage>Loading demographics...</LoadingMessage>
          ) : demographicsByCountry.length > 0 ? (
            <ChartContainer height={300}>
              <BarChart width={Math.min(chartWidth, 600)} height={300} data={demographicsByCountry.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="country" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="activeUsers" fill="#8884d8" name="Active Users" />
              </BarChart>
            </ChartContainer>
          ) : (
            <EmptyMessage>No demographics data available</EmptyMessage>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle>Users by Device {realTime?.data?.byDevice ? '(Real-time)' : ''}</ChartTitle>
          {loading.realTime || loading.demographics ? (
            <LoadingMessage>Loading device data...</LoadingMessage>
          ) : deviceCategoryData.length > 0 ? (
            <ChartContainer height={300}>
              <PieChart width={Math.min(chartWidth, 600)} height={300}>
                <Pie
                  data={deviceCategoryData}
                  cx={Math.min(chartWidth, 600) / 2}
                  cy={150}
                  labelLine={false}
                  label={({ device, percent }) => `${device}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deviceCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ChartContainer>
          ) : (
            <EmptyMessage>No device data available</EmptyMessage>
          )}
        </ChartCard>
      </ChartsGrid>
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const DateRangeSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--accent-secondary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatIcon = styled.div`
  font-size: 2rem;
  color: var(--accent-primary);
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ChartCard = styled.div`
  padding: 1.5rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const ChartTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
`;

const EmptyMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: var(--danger-color);

  p {
    margin-bottom: 1rem;
  }
`;

export default AnalyticsTab;

